import PageHeader from "../components/PageHeader";
import SEOMeta from "../components/SEOMeta";

const STAGES = [
  {
    n: "01",
    title: "Mandate alignment",
    body: "Every engagement begins with a clear understanding of what the client must deliver, and to whom. For state-owned enterprises and parastatals, that means starting with the principal-agent relationship between the organisation and the state. For private clients, it means starting with the board's expectation of strategic outcome. The output is a written mandate brief that the client signs off before substantive work begins."
  },
  {
    n: "02",
    title: "Evidence-based diagnosis",
    body: "We work from data, document review, structured interviews, and benchmark analysis to develop a clear-eyed view of the situation. We do not begin with a hypothesis we are looking to confirm. The diagnosis is shared in a working session with the client — not as a finished deck, but as a working document that the client helps shape."
  },
  {
    n: "03",
    title: "Strategy and design",
    body: "We develop strategy options that are testable, costed, and sequenced. Each option is presented with its commercial logic, its risks, its dependencies, and the trade-offs the client will be asked to accept. We help the client choose. We do not present a single answer disguised as a menu."
  },
  {
    n: "04",
    title: "Implementation support",
    body: "Most of our engagements include a defined implementation phase. That may be a programme management office, an embedded advisor, a delivery cadence we run with the executive, or a combination. We are committed to remaining accountable through delivery, with hand-over criteria agreed in advance."
  },
  {
    n: "05",
    title: "Knowledge transfer",
    body: "We build internal capability as we work. The end of the engagement is not the end of the strategy — it is the moment the client team owns it. We design the engagement so the materials, methods, and decision frameworks remain useful to the client long after we leave."
  }
];

export default function Approach() {
  return (
    <div data-testid="approach-page">
      <SEOMeta
        title="Our Approach"
        description="Learn how Dugavu Ventures' global consulting approach — from mandate alignment to implementation — delivers corporate strategy results for mining, oil and gas, and SOE clients."
        url="/approach"
      />
      <PageHeader
        eyebrow="Approach"
        title="Strategy must be mandated, evidence-based, sequenced, and delivered."
        lede="Our method is shaped by a simple conviction: strategy is only useful when it is mandated, evidence-based, sequenced, and delivered. We follow a five-stage approach across most engagements, adapted to the scope and timetable of the mandate."
        image="https://images.pexels.com/photos/6077872/pexels-photo-6077872.jpeg?auto=compress&cs=tinysrgb&w=1800&q=80"
        imageAlt="Man in suit reading a document"
        imageWrapperClassName="ring-2 ring-rule"
        testid="approach-header"
      />

      <section className="py-20 md:py-28">
        <div className="max-w-[1240px] mx-auto px-6 md:px-10 lg:px-14">
          {STAGES.map((s, i) => (
            <article
              key={s.n}
              className={`grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 py-12 md:py-16 ${i !== 0 ? "border-t border-rule" : "border-t border-rule"}`}
              data-testid={`stage-${s.n}`}
            >
              <div className="md:col-span-3">
                <div className="numeral text-5xl md:text-6xl">{s.n}</div>
              </div>
              <div className="md:col-span-9">
                <h3 className="font-serif text-3xl md:text-4xl text-ink leading-tight">{s.title}</h3>
                <p className="mt-5 text-charcoal text-lg leading-relaxed max-w-3xl">{s.body}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Engagement governance + Confidentiality */}
      <section className="py-24 md:py-32 bg-subtle" data-testid="approach-governance">
        <div className="max-w-[1240px] mx-auto px-6 md:px-10 lg:px-14">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20">
            <div>
              <h3 className="font-serif text-3xl md:text-4xl text-ink leading-tight border-t border-oxblood pt-6">
                Engagement governance
              </h3>
              <p className="mt-6 text-charcoal text-lg leading-relaxed">
                Every engagement is governed by a steering committee chaired by the client. We provide a senior partner as engagement lead, an engagement manager, and a delivery team scaled to the work. Status reporting is fortnightly, with a written reflection at the end of each phase.
              </p>
            </div>

            <div>
              <h3 className="font-serif text-3xl md:text-4xl text-ink leading-tight border-t border-oxblood pt-6">
                Confidentiality and conflict
              </h3>
              <p className="mt-6 text-charcoal text-lg leading-relaxed">
                We hold our work in strict confidence, and we manage conflict of interest actively. We will not accept an advisory mandate where we hold a competing operational interest, nor will we share information across mandates without explicit written consent.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
